import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Paper,
  CircularProgress,
  Button,
  Alert,
  Modal,
  Backdrop,
  Fab,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TodayIcon from "@mui/icons-material/Today";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getImagesByDate, getAvailableDates } from "../services/storageService";
import { getPreviousDay, getNextDay } from "../utils/dateUtils";
import { useAuth } from "../contexts/AuthContext";
import { debounce } from "lodash";
import { format, parseISO } from "date-fns";

// Custom styles for date picker
const datePickerStyles = `
  .available-date {
    background-color: #e8f5e8 !important;
    color: #2e7d32 !important;
    font-weight: bold !important;
  }
  
  .available-date:hover {
    background-color: #c8e6c9 !important;
  }
  
  .unavailable-date {
    background-color: #ffebee !important;
    color: #c62828 !important;
    cursor: not-allowed !important;
  }
  
  .unavailable-date:hover {
    background-color: #ffcdd2 !important;
  }
  
  .react-datepicker__day--disabled {
    background-color: #f5f5f5 !important;
    color: #bdbdbd !important;
    cursor: not-allowed !important;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = datePickerStyles;
  document.head.appendChild(styleElement);
}

const DailyImages = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [availableDates, setAvailableDates] = useState([]);
  const [availableDatesLoading, setAvailableDatesLoading] = useState(false);
  const [showDailyImages, setShowDailyImages] = useState(false);
  const [autoUpdateInterval, setAutoUpdateInterval] = useState(null);
  const { isAuthenticated } = useAuth();

  // Fetch available dates
  const fetchAvailableDates = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setAvailableDatesLoading(true);
      const dates = await getAvailableDates();
      setAvailableDates(dates);
    } catch (err) {
      console.error("Error fetching available dates:", err);
    } finally {
      setAvailableDatesLoading(false);
    }
  }, [isAuthenticated]);

  // Check if a date has images available
  const isDateAvailable = (date) => {
    const dateString = format(date, "yyyy-MM-dd");
    return availableDates.includes(dateString);
  };

  // Custom day class name function for react-datepicker
  const getDayClassName = (date) => {
    if (isDateAvailable(date)) {
      return "available-date";
    }
    return "unavailable-date";
  };

  // Filter function to disable unavailable dates
  const filterDate = (date) => {
    return isDateAvailable(date);
  };

  const fetchImages = useCallback(
    async (dateToFetch) => {
      if (!isAuthenticated) {
        setError("You must be logged in to view images.");
        setImages([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);

      try {
        const fetchedImages = await getImagesByDate(dateToFetch);
        setImages(fetchedImages);
        if (fetchedImages.length === 0) {
          console.log(
            `No images found for ${format(dateToFetch, "yyyy-MM-dd")}`
          );
        }
      } catch (err) {
        console.error("Error fetching daily images:", err);
        setError(
          err.message || "Failed to fetch images for the selected date."
        );
        setImages([]);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated]
  );

  const debouncedFetchImages = useCallback(
    debounce((date) => {
      fetchImages(date);
    }, 500),
    [fetchImages]
  );

  useEffect(() => {
    if (isAuthenticated && showDailyImages) {
      fetchImages(selectedDate);
    } else {
      setImages([]);
      if (!showDailyImages) {
        setError(null); // Clear error when toggle is off
      } else {
        setError("Please log in to view images.");
      }
    }
  }, [isAuthenticated, fetchImages, selectedDate, showDailyImages]);

  // Fetch available dates when authenticated and toggle is ON
  useEffect(() => {
    if (isAuthenticated && showDailyImages) {
      fetchAvailableDates();
    }
  }, [isAuthenticated, fetchAvailableDates, showDailyImages]);

  // Auto-update logic - sync with Latest Image timing (every ~5 minutes)
  useEffect(() => {
    // Clear existing interval
    if (autoUpdateInterval) {
      clearInterval(autoUpdateInterval);
    }

    const isAutoUpdateActive =
      showDailyImages && isToday() && !carouselOpen && isAuthenticated;

    if (isAutoUpdateActive) {
      // Set up auto-update every 5 minutes and 10 seconds (same as Latest Image)
      const interval = setInterval(() => {
        console.log("🔄 Auto-updating daily images for today");
        fetchImages(selectedDate);
      }, (5 * 60 + 10) * 1000);

      setAutoUpdateInterval(interval);
    }

    return () => {
      if (autoUpdateInterval) {
        clearInterval(autoUpdateInterval);
      }
    };
  }, [
    showDailyImages,
    selectedDate,
    carouselOpen,
    isAuthenticated,
    fetchImages,
  ]);

  useEffect(() => {
    if (isAuthenticated && showDailyImages) {
      debouncedFetchImages(selectedDate);
    }
    return () => {
      debouncedFetchImages.cancel();
    };
  }, [selectedDate, isAuthenticated, debouncedFetchImages, showDailyImages]);

  // Keyboard navigation for carousel
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!carouselOpen) return;

      if (event.key === "ArrowLeft") {
        goToPreviousImage();
      } else if (event.key === "ArrowRight") {
        goToNextImage();
      } else if (event.key === "Escape") {
        setCarouselOpen(false);
      } else if (event.key === "+" || event.key === "=") {
        zoomIn();
      } else if (event.key === "-") {
        zoomOut();
      } else if (event.key === "0") {
        resetZoom();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [carouselOpen, currentImageIndex, images.length, zoomLevel]);

  // Mouse wheel zoom support
  useEffect(() => {
    const handleWheel = (event) => {
      if (!carouselOpen) return;

      event.preventDefault();
      if (event.deltaY < 0) {
        zoomIn();
      } else {
        zoomOut();
      }
    };

    if (carouselOpen) {
      window.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => window.removeEventListener("wheel", handleWheel);
  }, [carouselOpen, zoomLevel]);

  const goToPreviousDay = useCallback(() => {
    if (availableDates.length === 0) {
      setSelectedDate((prevDate) => getPreviousDay(prevDate));
      return;
    }

    const currentDateStr = format(selectedDate, "yyyy-MM-dd");
    const currentIndex = availableDates.indexOf(currentDateStr);

    if (currentIndex > 0) {
      // Go to previous available date
      const previousDateStr = availableDates[currentIndex - 1];
      setSelectedDate(parseISO(previousDateStr));
    } else {
      // Find the latest available date before current date
      const previousAvailableDate = availableDates.find(
        (dateStr) => dateStr < currentDateStr
      );
      if (previousAvailableDate) {
        setSelectedDate(parseISO(previousAvailableDate));
      }
    }
  }, [selectedDate, availableDates]);

  const goToNextDay = useCallback(() => {
    if (availableDates.length === 0) {
      setSelectedDate((prevDate) => getNextDay(prevDate));
      return;
    }

    const currentDateStr = format(selectedDate, "yyyy-MM-dd");
    const currentIndex = availableDates.indexOf(currentDateStr);

    if (currentIndex >= 0 && currentIndex < availableDates.length - 1) {
      // Go to next available date
      const nextDateStr = availableDates[currentIndex + 1];
      setSelectedDate(parseISO(nextDateStr));
    } else {
      // Find the earliest available date after current date
      const nextAvailableDate = availableDates.find(
        (dateStr) => dateStr > currentDateStr
      );
      if (nextAvailableDate) {
        setSelectedDate(parseISO(nextAvailableDate));
      }
    }
  }, [selectedDate, availableDates]);

  const goToToday = useCallback(() => {
    const today = new Date();
    const todayStr = format(today, "yyyy-MM-dd");

    if (availableDates.includes(todayStr)) {
      setSelectedDate(today);
    } else if (availableDates.length > 0) {
      // Go to the most recent available date
      const mostRecentDate = availableDates[0]; // availableDates is sorted descending
      setSelectedDate(parseISO(mostRecentDate));
    } else {
      setSelectedDate(today);
    }
  }, [availableDates]);

  const handleDateChange = useCallback((date) => {
    setSelectedDate(date);
    setIsDatePickerOpen(false);
  }, []);

  const toggleDatePicker = useCallback(() => {
    setIsDatePickerOpen((prev) => !prev);
  }, []);

  const openCarousel = useCallback((index) => {
    setCurrentImageIndex(index);
    setCarouselOpen(true);
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  const closeCarousel = useCallback(() => {
    setCarouselOpen(false);
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  const goToPreviousImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  }, [images.length]);

  const goToNextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  }, [images.length]);

  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  // Mouse drag handlers for panning when zoomed
  const handleMouseDown = useCallback(
    (event) => {
      if (zoomLevel > 1) {
        setIsDragging(true);
        setDragStart({
          x: event.clientX - imagePosition.x,
          y: event.clientY - imagePosition.y,
        });
      }
    },
    [zoomLevel, imagePosition]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (isDragging && zoomLevel > 1) {
        setImagePosition({
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart, zoomLevel]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Check if selected date is today
  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  // Filter out images with errors for display
  const validImages = images.filter(
    (image) => image && image.metadata && image.url
  );

  // Error boundary for debugging
  try {
    if (!isAuthenticated && !isLoading && showDailyImages) {
      return (
        <Box sx={{ mt: 4 }}>
          {/* Daily Images Header with Toggle */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant='h4' component='h2'>
                Daily Images
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={showDailyImages}
                    onChange={(e) => setShowDailyImages(e.target.checked)}
                    color='primary'
                  />
                }
                label='Show'
                sx={{ ml: 1 }}
              />
            </Box>
          </Box>

          <Paper sx={{ p: 3, textAlign: "center", bgcolor: "grey.100" }}>
            <Typography variant='h6' color='text.secondary'>
              Please log in to view daily images.
            </Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 4 }}>
        {/* Daily Images Header with Toggle */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography variant='h4' component='h2'>
              Daily Images
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showDailyImages}
                  onChange={(e) => setShowDailyImages(e.target.checked)}
                  color='primary'
                />
              }
              label='Show'
              sx={{ ml: 1 }}
            />
          </Box>

          {!showDailyImages ? (
            <Typography variant='body2' color='text.secondary'>
              Toggle "Show" to browse webcam images by date and enable
              auto-updates for today.
            </Typography>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Browse through webcam images by date. Use the navigation controls
              to change the date.
              {isToday() &&
                " Auto-updates every ~5 minutes when viewing today."}
            </Typography>
          )}
        </Box>

        {/* Only render the rest if toggle is ON */}
        {showDailyImages && (
          <>
            <Paper
              sx={{ p: 2, mb: 3, position: "sticky", top: 64, zIndex: 100 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <IconButton
                  onClick={goToPreviousDay}
                  aria-label='Previous day'
                  disabled={isLoading || availableDatesLoading}
                >
                  <ArrowBackIcon />
                </IconButton>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <Button
                    onClick={toggleDatePicker}
                    variant='text'
                    startIcon={
                      availableDatesLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CalendarTodayIcon />
                      )
                    }
                    disabled={isLoading || availableDatesLoading}
                    sx={{ textTransform: "none", fontSize: "1rem" }}
                  >
                    {format(selectedDate, "PPP")}
                  </Button>

                  {isDatePickerOpen && !availableDatesLoading && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 1100,
                        mt: 1,
                      }}
                    >
                      <Paper elevation={3}>
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          inline
                          filterDate={filterDate}
                          getDayClassName={getDayClassName}
                        />
                      </Paper>
                    </Box>
                  )}
                </Box>

                <IconButton
                  onClick={goToNextDay}
                  aria-label='Next day'
                  disabled={
                    isLoading ||
                    availableDatesLoading ||
                    new Date(selectedDate).setHours(0, 0, 0, 0) >=
                      new Date().setHours(0, 0, 0, 0)
                  }
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
            </Paper>

            {/* Go to Today button - only visible when not viewing today */}
            {!isToday() && (
              <Fab
                color='primary'
                aria-label='go to today'
                onClick={goToToday}
                sx={{
                  position: "fixed",
                  bottom: 16,
                  right: 16,
                  zIndex: 1000,
                }}
              >
                <TodayIcon />
              </Fab>
            )}

            {error && (
              <Alert severity='error' sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "300px",
                  gap: 2,
                }}
              >
                <CircularProgress size={50} />
                <Typography>
                  Loading images for {format(selectedDate, "PPP")}...
                </Typography>
              </Box>
            ) : validImages.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: "center", bgcolor: "grey.100" }}>
                <Typography variant='h6' color='text.secondary'>
                  No images available for {format(selectedDate, "PPP")}.
                </Typography>
              </Paper>
            ) : (
              <>
                <Typography variant='h6' sx={{ mb: 2 }}>
                  {validImages.length} images for {format(selectedDate, "PPP")}
                  {isToday() &&
                    showDailyImages &&
                    isAuthenticated &&
                    !carouselOpen && (
                      <Typography
                        component='span'
                        variant='caption'
                        color='primary'
                        sx={{ ml: 1 }}
                      >
                        (Auto-updating)
                      </Typography>
                    )}
                </Typography>

                <Grid container spacing={2}>
                  {validImages.map((image, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={image.metadata.id || image.metadata.filename}
                    >
                      <Card
                        sx={{
                          cursor: "pointer",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          "&:hover": {
                            transform: "scale(1.02)",
                            boxShadow: 4,
                          },
                        }}
                        onClick={() => openCarousel(index)}
                      >
                        <CardMedia
                          component='img'
                          image={image.url}
                          alt={`Webcam view from ${format(
                            new Date(
                              new Date(image.metadata.timestamp).getTime() -
                                2 * 60 * 60 * 1000
                            ),
                            "dd.MM.yyyy HH:mm"
                          )}`}
                          sx={{ height: 220, objectFit: "cover" }}
                          loading='lazy'
                        />
                        <CardContent sx={{ pt: 1, pb: "16px !important" }}>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            noWrap
                          >
                            Time:{" "}
                            {format(
                              new Date(
                                new Date(image.metadata.timestamp).getTime() -
                                  2 * 60 * 60 * 1000
                              ),
                              "HH:mm"
                            )}
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            display='block'
                            noWrap
                          >
                            {image.metadata.filename}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}

        {/* Carousel Modal */}
        <Modal
          open={carouselOpen}
          onClose={closeCarousel}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
            sx: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "95vw",
              height: "95vh",
              display: "flex",
              flexDirection: "column",
              outline: "none",
            }}
          >
            {/* Header with close button and image info */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                color: "white",
              }}
            >
              <Typography variant='h6'>
                {validImages[currentImageIndex] &&
                  format(
                    new Date(
                      new Date(
                        validImages[currentImageIndex].metadata.timestamp
                      ).getTime() -
                        2 * 60 * 60 * 1000
                    ),
                    "dd.MM.yyyy HH:mm"
                  )}{" "}
                ({currentImageIndex + 1} of {validImages.length})
              </Typography>

              {/* Zoom controls */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant='body2' sx={{ color: "white", mr: 1 }}>
                  {Math.round(zoomLevel * 100)}%
                </Typography>
                <IconButton
                  onClick={zoomOut}
                  disabled={zoomLevel <= 0.5}
                  sx={{ color: "white" }}
                >
                  <ZoomOutIcon />
                </IconButton>
                <IconButton onClick={resetZoom} sx={{ color: "white" }}>
                  <RestartAltIcon />
                </IconButton>
                <IconButton
                  onClick={zoomIn}
                  disabled={zoomLevel >= 3}
                  sx={{ color: "white" }}
                >
                  <ZoomInIcon />
                </IconButton>
                <IconButton
                  onClick={closeCarousel}
                  sx={{ color: "white", ml: 2 }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Main image area */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Previous button */}
              <IconButton
                onClick={goToPreviousImage}
                sx={{
                  position: "absolute",
                  left: 20,
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  zIndex: 1,
                }}
              >
                <KeyboardArrowLeftIcon fontSize='large' />
              </IconButton>

              {/* Image */}
              {validImages[currentImageIndex] && (
                <img
                  src={validImages[currentImageIndex].url}
                  alt={`Webcam image ${currentImageIndex + 1}`}
                  style={{
                    maxWidth: zoomLevel > 1 ? "none" : "100%",
                    maxHeight: zoomLevel > 1 ? "none" : "100%",
                    width: zoomLevel > 1 ? `${zoomLevel * 100}%` : "auto",
                    height: zoomLevel > 1 ? `${zoomLevel * 100}%` : "auto",
                    objectFit: "contain",
                    transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                    cursor:
                      zoomLevel > 1
                        ? isDragging
                          ? "grabbing"
                          : "grab"
                        : "default",
                    userSelect: "none",
                    transition: isDragging ? "none" : "transform 0.1s ease",
                  }}
                  draggable={false}
                />
              )}

              {/* Next button */}
              <IconButton
                onClick={goToNextImage}
                sx={{
                  position: "absolute",
                  right: 20,
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                  zIndex: 1,
                }}
              >
                <KeyboardArrowRightIcon fontSize='large' />
              </IconButton>
            </Box>

            {/* Footer with navigation hint */}
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                color: "white",
              }}
            >
              <Typography variant='body2'>
                Use arrow keys or click buttons to navigate • Mouse wheel or +/-
                to zoom • Drag to pan when zoomed • Press ESC to close
              </Typography>
            </Box>
          </Box>
        </Modal>
      </Box>
    );
  } catch (error) {
    console.error("❌ Error in DailyImages component:", error);
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant='h4' component='h2' gutterBottom>
          Daily Images
        </Typography>
        <Alert severity='error'>
          Error loading Daily Images component. Check console for details.
        </Alert>
      </Box>
    );
  }
};

export default DailyImages;
